import { motion } from 'framer-motion';
import { useState } from 'react';
import { Character } from '@/data/characters';
import { cn } from '@/lib/utils';

interface CharacterPortraitProps {
  character: Character;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  selected?: boolean;
  winner?: boolean;
  loser?: boolean;
  dimmed?: boolean;
  onClick?: () => void;
  showName?: boolean;
  showVerse?: boolean;
  className?: string;
  priority?: boolean; // For preloading important images
}

const sizeClasses = {
  xs: 'w-16 h-16',
  sm: 'w-24 h-24',
  md: 'w-36 h-36',
  lg: 'w-48 h-48',
  xl: 'w-72 h-72',
};

export function CharacterPortrait({
  character,
  size = 'md',
  selected = false,
  winner = false,
  loser = false,
  dimmed = false,
  onClick,
  showName = true,
  showVerse = false,
  className,
  priority = false,
}: CharacterPortraitProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const portraitUrl = encodeURI(character.portrait);

  return (
    <motion.div
      className={cn(
        'portrait-card',
        sizeClasses[size],
        selected && 'selected',
        winner && 'winner',
        loser && 'loser',
        dimmed && 'opacity-50',
        onClick ? 'cursor-pointer hover:scale-105' : 'cursor-default',
        className
      )}
      onClick={onClick}
      whileHover={onClick ? { scale: 1.05 } : undefined}
      whileTap={onClick ? { scale: 0.98 } : undefined}
      layout
    >
      {/* Character image with optimized loading */}
      <div className="absolute inset-0 overflow-hidden rounded-xl">
        {/* Loading placeholder */}
        {!imageLoaded && !imageError && (
          <div className="w-full h-full bg-secondary/50 animate-pulse flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        
        {/* Actual image */}
        <img
          src={portraitUrl}
          alt={character.name}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          className={cn(
            'w-full h-full object-cover transition-opacity duration-300',
            imageLoaded ? 'opacity-100' : 'opacity-0'
          )}
          style={{
            maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 50%, rgba(0,0,0,0.3) 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 50%, rgba(0,0,0,0.3) 100%)',
          }}
        />
        
        {/* Error fallback */}
        {imageError && (
          <div className="w-full h-full bg-secondary flex items-center justify-center">
            <span className="text-muted-foreground text-sm">Image unavailable</span>
          </div>
        )}
        
        {/* Gradient overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/80" />
      </div>
      
      {/* Name overlay */}
      {showName && (
        <div className="absolute bottom-0 left-0 right-0 p-2 text-center z-10">
          <h3 className="font-display font-bold text-foreground text-sm truncate drop-shadow-lg">
            {character.name}
          </h3>
          {showVerse && (
            <p className="text-xs text-muted-foreground truncate drop-shadow-md">
              {character.verse}
            </p>
          )}
        </div>
      )}
      
      {/* Selection glow overlay */}
      {selected && (
        <motion.div
          className="absolute inset-0 rounded-xl pointer-events-none ring-2 ring-primary ring-offset-2 ring-offset-background"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            background: 'radial-gradient(circle at center, hsl(var(--primary) / 0.3) 0%, transparent 70%)',
          }}
        />
      )}
      
      {/* Winner badge */}
      {winner && (
        <motion.div
          className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-success flex items-center justify-center text-lg shadow-lg z-20"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', duration: 0.5 }}
        >
          ✓
        </motion.div>
      )}
    </motion.div>
  );
}
